import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import { getOptimizedSelector } from "store/utils/get-optimized-selector.utils";
import { registerStore } from "store/utils/reset-store.utils";

type ShowEffectiveLanguages = {
  languageCodes: string[];
  translatableLanguageCodes: string[];
  defaultLanguage: string;
  adminDisabledLanguageCodes: string[];
};

type State = {
  languageCodes: string[];
  hasAdditionalLanguages: boolean;
  isLoading: boolean;
  hasFetched: boolean;
  effectiveByShowId: Record<string, ShowEffectiveLanguages>;
  effectiveLoadingShowId: string | null;
};

type Action = {
  fetch: (force?: boolean) => Promise<void>;
  fetchEffective: (showId: string, force?: boolean) => Promise<void>;
  reset: () => void;
};

const DEFAULT_STATE: State = {
  languageCodes: [],
  hasAdditionalLanguages: false,
  isLoading: false,
  hasFetched: false,
  effectiveByShowId: {},
  effectiveLoadingShowId: null,
};

export const exhibitorAvailableLanguagesStore = create(
  devtools<State & Action>((set, get) => ({
    ...DEFAULT_STATE,
    fetch: async (force = false) => {
      const { isLoading, hasFetched } = get();

      if (!force && (isLoading || hasFetched)) {
        return;
      }

      set({ isLoading: true });

      try {
        const {
          languageCodes,
          hasAdditionalLanguages,
        } = await apiV2.localization.getExhibitorAvailableLanguages();

        set({
          languageCodes,
          hasAdditionalLanguages,
          isLoading: false,
          hasFetched: true,
        });
      } catch (e) {
        console.error(
          "Error occurred fetching exhibitor available languages: ",
          e
        );
        set({ isLoading: false });
      }
    },
    fetchEffective: async (showId, force = false) => {
      const { effectiveByShowId, effectiveLoadingShowId } = get();

      if (
        !force &&
        (effectiveLoadingShowId === showId || effectiveByShowId[showId])
      ) {
        return;
      }

      set({ effectiveLoadingShowId: showId });

      try {
        const {
          languageCodes,
          translatableLanguageCodes,
          defaultLanguage,
          adminDisabledLanguageCodes,
        } = await apiV2.localization.getEffectiveShowLanguages({ showId });

        set((state) => ({
          effectiveByShowId: {
            ...state.effectiveByShowId,
            [showId]: {
              languageCodes,
              translatableLanguageCodes,
              defaultLanguage,
              adminDisabledLanguageCodes: adminDisabledLanguageCodes ?? [],
            },
          },
          effectiveLoadingShowId: null,
        }));
      } catch (e) {
        console.error("Error occurred fetching effective show languages: ", e);
        set({ effectiveLoadingShowId: null });
      }
    },
    reset: () => set(DEFAULT_STATE),
  }))
);

registerStore(exhibitorAvailableLanguagesStore);

export const useExhibitorAvailableLanguagesStore = getOptimizedSelector(
  exhibitorAvailableLanguagesStore
);
